/* Video wall: four vertical customer clips that autoplay muted, with a per-card
   sound toggle. Nothing is downloaded until a card scrolls into view. */
(function () {
  if (customElements.get('pdp-ugc-wall')) return;

  const REDUCED = window.matchMedia('(prefers-reduced-motion: reduce)');

  class UgcWall extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('.pdp-ugc__card'));
      this.cards.forEach((card) => this.setupCard(card));

      // Autoplay is a convenience, not the point of the section: when the
      // visitor has asked for reduced motion we hand them the browser's own
      // controls instead and let them start it themselves.
      if (REDUCED.matches) return;

      this.observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            const video = entry.target.querySelector('.pdp-ugc__video');
            if (!video) return;
            entry.target.dataset.visible = entry.isIntersecting ? '1' : '';
            if (entry.isIntersecting) {
              this.load(video);
              this.play(video);
            } else {
              video.pause();
            }
          });
        },
        // A little margin so the file is already in flight by the time the row
        // is actually on screen, rather than starting to fetch on arrival.
        { threshold: 0.1, rootMargin: '200px 0px' }
      );
      this.cards.forEach((card) => this.observer.observe(card));
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }

    setupCard(card) {
      const video = card.querySelector('.pdp-ugc__video');
      const button = card.querySelector('.pdp-ugc__sound');
      if (!video) return;

      video.muted = true;
      video.volume = 1;

      if (REDUCED.matches) {
        this.load(video);
        video.controls = true;
        card.classList.add('pdp-ugc__card--controls');
        if (button) button.hidden = true;
        return;
      }

      if (button) {
        button.addEventListener('click', () => this.toggleSound(video, button));
      }
    }

    load(video) {
      if (video.dataset.loaded) return;
      video.dataset.loaded = '1';
      video.src = video.dataset.src;
      // preload="none" means readyState is 0 at this point, so the play() that
      // follows is a request rather than a start. If it is still paused once
      // there are frames to show, ask again — the card is on screen by then.
      video.addEventListener(
        'loadeddata',
        () => {
          const card = video.closest('.pdp-ugc__card');
          if (video.paused && card && card.dataset.visible) this.play(video);
        },
        { once: true }
      );
    }

    play(video) {
      const attempt = video.play();
      if (!attempt || !attempt.catch) return;
      attempt.catch((error) => {
        // A play() cut short by the card scrolling away aborts; that is normal
        // and the next intersection will start it again. Anything else means
        // the browser refused autoplay, so give the visitor real controls
        // rather than a frozen poster with no way in.
        if (error && error.name === 'AbortError') return;
        video.controls = true;
        video.closest('.pdp-ugc__card').classList.add('pdp-ugc__card--controls');
      });
    }

    toggleSound(video, button) {
      const turningOn = video.muted;

      if (turningOn) {
        // Only one clip may be audible; four talking heads at once is unusable.
        this.cards.forEach((card) => {
          const other = card.querySelector('.pdp-ugc__video');
          if (other && other !== video) this.setMuted(other, true);
        });
      }

      this.setMuted(video, !turningOn);
      if (turningOn) {
        this.load(video);
        this.play(video);
      }
      button.focus();
    }

    setMuted(video, muted) {
      video.muted = muted;
      const card = video.closest('.pdp-ugc__card');
      const button = card && card.querySelector('.pdp-ugc__sound');
      if (!button) return;
      card.classList.toggle('pdp-ugc__card--unmuted', !muted);
      button.setAttribute('aria-pressed', muted ? 'false' : 'true');
      button.setAttribute(
        'aria-label',
        muted ? button.dataset.labelUnmute : button.dataset.labelMute
      );
    }
  }

  customElements.define('pdp-ugc-wall', UgcWall);
})();
